import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { DealDto, HoldingDto } from '@78/shared';
import { refundPercentOf } from '@78/core';
import { fetchCollections, fetchHoldings, fetchOffers } from '../lib/api.ts';
import { sameAddress, shortAddress, useCollectionNames } from '../lib/collections.ts';
import { useErrorText } from '../lib/errors.ts';
import { GiftArt } from '../components/GiftArt.tsx';
import { formatAmount, ticker } from '@78/i18n';
import { useFmt } from '../lib/format.tsx';
import { Positions } from './Positions.tsx';
import { CollectionPicker } from '../components/CollectionPicker.tsx';

/** Сколько коллекций показывать фишками. Дальше — список с поиском: лента,
 *  уезжающая за край экрана, прячет нужное не хуже отдельного экрана. */
const CHIPS_SHOWN = 4;

/** Платёж в период — то число, по которому реально выбирают оффер. */
function perPayment(d: DealDto): bigint {
  const total = BigInt(d.terms.totalPrice);
  const upfront = BigInt(d.terms.upfront);
  return (total - upfront) / BigInt(d.terms.nPayments);
}

/**
 * Чужие NFT, чьи владельцы здесь есть.
 *
 * Прошлая витрина зеркалила лоты чужой площадки и умерла от того, что из
 * 1661 владельца в приложении не было ни одного: показывать товар,
 * хозяину которого не написать, — значит звать человека в тупик. Здесь
 * обратное: каждая вещь в списке принадлежит тому, до кого письмо дойдёт,
 * и запрос уходит ему лично.
 *
 * Поэтому и заголовок не про продажу. Владелец ничего не выставлял — он
 * просто владеет вещью; обещать «это продаётся» значило бы повторить ту же
 * ошибку, только уже на своих людях.
 */
function HoldingCard({
  item,
  floor,
  onAsk,
}: {
  item: HoldingDto;
  floor: string | null;
  onAsk: () => void;
}) {
  const { t } = useTranslation();
  const { lang } = useFmt();

  /**
   * Подпись под именем — одна строка и всегда про деньги.
   *
   * Цена выставленного лота важнее флора: это то, за сколько вещь отдают
   * прямо сейчас, и именно от неё человек отталкивается, называя условия
   * рассрочки. Нет цены — остаётся флор коллекции: своей цены у вещи в
   * таком случае нет, владелец ничего не назначал.
   */
  const price =
    item.onSale && item.salePrice !== null
      ? `${t('market.on_sale_now')} · ${formatAmount(BigInt(item.salePrice), item.saleDecimals, lang)} ${ticker(item.saleCurrency)}`
      : item.onSale
        ? t('market.on_sale_now')
        : floor === null
          ? ''
          : `${t('market.floor_short')} ${floor}`;

  return (
    <div className="card col" style={{ padding: 10, gap: 9, minWidth: 0 }}>
      <GiftArt image={item.image} size={88} radius={11} full />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.name ?? shortAddress(item.item)}
        </div>
        <div
          className="label"
          style={{
            marginTop: 3,
            color: item.onSale ? 'var(--lime-text)' : 'var(--ink3)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {price}
        </div>
      </div>
      <button className="btn" style={{ padding: 9, fontSize: 11.5 }} onClick={onAsk}>
        {t('market.want_installments')}
      </button>
    </div>
  );
}

/**
 * Одна выборка на два места: заголовок-вкладка показывает число, список —
 * сами вещи. Ключ общий, поэтому запрос уходит один.
 */
function useHoldings(collection: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['holdings', collection ?? 'all'],
    queryFn: () => fetchHoldings(collection),
    staleTime: 60_000,
    enabled,
  });
}

function Holdings({
  collection,
  floorOf,
  onAsk,
}: {
  collection: string | null;
  floorOf: (collection: string) => string | null;
  onAsk: (item: HoldingDto) => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useHoldings(collection, true);

  const items = data?.items ?? [];

  return (
    <>
      <span className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>{t('market.can_ask_hint')}</span>

      {isLoading && <div className="skeleton" style={{ minHeight: 150 }} />}

      {!isLoading && items.length === 0 && (
        <div className="card col">
          <strong className="title">{t('market.no_holdings')}</strong>
          <span className="text">{t('market.no_holdings_hint')}</span>
        </div>
      )}

      <div className="grid2">
        {items.map((item) => (
          <HoldingCard
            key={item.item}
            item={item}
            floor={floorOf(item.collection)}
            onAsk={() => onAsk(item)}
          />
        ))}
      </div>
    </>
  );
}

/**
 * Карточка оффера — раскладка из макета: превью гифта, имя с ценой, метка
 * уровня возврата справа и две плитки с цифрами, по которым выбирают.
 */
export function OfferCard({ deal, onOpen }: { deal: DealDto; onOpen: (a: string) => void }) {
  const { t } = useTranslation();
  const { sum, money } = useFmt();

  return (
    <button
      className="card"
      onClick={() => onOpen(deal.address)}
      style={{ display: 'flex', gap: 13, padding: 12, borderRadius: 16, textAlign: 'left', cursor: 'pointer', color: 'var(--ink)', font: 'inherit' }}
    >
      <GiftArt image={deal.nftImage} size={84} />

      <div className="col" style={{ flex: 1, minWidth: 0, gap: 8 }}>
        <div className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {deal.nftName ?? shortAddress(deal.nftAddress)}
            </div>
            <div className="mono dim" style={{ fontSize: 10, marginTop: 2 }}>
              {sum(deal.terms.totalPrice)}
            </div>
          </div>
          <span className="chip">{String(refundPercentOf(deal.terms.refundTier))}%</span>
        </div>

        <div className="stats2">
          <div className="stat">
            <div className="label label-xs">{t('market.down')}</div>
            <div className="stat-value">{money(deal.terms.upfront)}</div>
          </div>
          <div className="stat">
            <div className="label label-xs">{t('market.per_week')}</div>
            <div className="stat-value">{money(perPayment(deal))}</div>
          </div>
        </div>
      </div>
    </button>
  );
}

export function Market({
  onOpen,
  onRequest,
  invite,
  onInviter,
}: {
  onOpen: (address: string) => void;
  onRequest: (collection: string, item?: { address: string; name: string | null }) => void;
  /** Код приглашения, если пришли по ссылке. Null — пришли сами. */
  invite: string | null;
  onInviter: () => void;
}) {
  const { t } = useTranslation();
  const { lang } = useFmt();
  const errorText = useErrorText();
  const nameOf = useCollectionNames();
  const [tab, setTab] = useState<'offers' | 'positions'>('offers');
  /**
   * Раздел внутри витрины: готовые офферы или вещи людей, которых можно
   * попросить. По умолчанию первое — там сделку можно начать в одно
   * нажатие, а просьба требует ответа другого человека.
   */
  const [sub, setSub] = useState<'offers' | 'holdings'>('offers');
  /** null — все коллекции. Отдельной вкладки «каталог» больше нет: она
   *  отвечала на тот же вопрос, что и витрина, только другой дверью. */
  const [pick, setPick] = useState<string | null>(null);
  /** Куда ведёт выбор из списка: сузить витрину или пойти просить рассрочку. */
  const [picking, setPicking] = useState<null | 'filter' | 'request'>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['offers'],
    queryFn: () => fetchOffers(),
    enabled: tab === 'offers',
  });

  // Коллекции реестра — это и фильтр, и источник флора: цена площадки нужна
  // ровно там, где человек смотрит на предложения и решает, дорого ли.
  const catalog = useQuery({
    queryKey: ['collections'],
    queryFn: fetchCollections,
    staleTime: 60_000,
    enabled: tab === 'offers',
  });
  const collections = catalog.data?.collections ?? [];
  const chosen = pick === null ? null : collections.find((c) => sameAddress(c.collection, pick));
  const offers = (data?.offers ?? []).filter(
    (d) => pick === null || sameAddress(d.collection, pick),
  );
  // Число у второй вкладки: без него человек не знает, стоит ли туда
  // заглядывать, а «Есть у людей» без цифры рядом с «Готовы к покупке · 3»
  // выглядит недоделкой.
  const held = useHoldings(pick, tab === 'offers');

  // Фишками показываем немногие: лента, уезжающая за край экрана, прячет
  // нужное ровно так же, как его прятал отдельный экран каталога. Остальные
  // — в списке с поиском, и выбранная коллекция всегда среди видимых.
  const chips = collections.slice(0, CHIPS_SHOWN);
  const hidden = collections.length - chips.length;
  const visible =
    chosen === undefined || chosen === null || chips.some((c) => c.collection === chosen.collection)
      ? chips
      : [chosen, ...chips.slice(0, CHIPS_SHOWN - 1)];

  return (
    <div className="screen">
      {/* Пришли по чужой ссылке — говорим об этом строкой, а не отдельным
          экраном: у пригласившего может не быть ничего, и встречать
          человека пустотой нельзя. Приглашение работает независимо от
          того, заглянет он к нему или нет. */}
      {invite !== null && (
        <button
          className="card row"
          onClick={onInviter}
          style={{ padding: '10px 12px', cursor: 'pointer', color: 'var(--ink)', font: 'inherit', textAlign: 'left' }}
        >
          <span className="text" style={{ fontSize: 12 }}>{t('inviter.came_by_link')}</span>
          <span className="label" style={{ whiteSpace: 'nowrap' }}>{t('inviter.look')} ›</span>
        </button>
      )}

      <div className="segmented" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <button aria-selected={tab === 'offers'} onClick={() => setTab('offers')}>{t('market.offers')}</button>
        <button aria-selected={tab === 'positions'} onClick={() => setTab('positions')}>
          {t('nav.positions_full')}
        </button>
      </div>

      {tab === 'positions' && <Positions />}

      {tab === 'offers' && (
        <>
          {/* Коллекции фильтром, а не разделом: выбор коллекции — это сужение
              витрины, а не другое место. */}
          {collections.length > 0 && (
            <div className="chips">
              <button aria-selected={pick === null} onClick={() => setPick(null)}>
                {t('market.all_collections')}
              </button>
              {visible.map((c) => (
                <button
                  key={c.collection}
                  aria-selected={pick !== null && sameAddress(c.collection, pick)}
                  onClick={() => setPick(c.collection)}
                >
                  {nameOf(c.collection).name ?? shortAddress(c.collection)}
                </button>
              ))}
              {hidden > 0 && (
                <button onClick={() => setPicking('filter')}>{t('market.more')} · {hidden}</button>
              )}
            </div>
          )}

          {/* Цена площадки по выбранной коллекции — то, с чем человек
              сравнивает предложение, прежде чем его принять. */}
          {chosen !== null && chosen !== undefined && (
            <div className="row">
              <span className="muted">
                {nameOf(chosen.collection).name ?? shortAddress(chosen.collection)} ·{' '}
                {t('market.floor_here')}
              </span>
              <span className="num">
                {chosen.marketFloor === null
                  ? '—'
                  : `${formatAmount(BigInt(chosen.marketFloor), chosen.marketDecimals, lang)} ${ticker(chosen.marketCurrency)}`}
              </span>
            </div>
          )}

          {/* Два раздела витрины: что уже предлагают и что можно попросить.
              Второе — не «ещё немного товара внизу», а другой способ купить,
              и стоять оно должно рядом, а не под первым. */}
          <div className="headtabs">
            <button aria-selected={sub === 'offers'} onClick={() => setSub('offers')}>
              {t('market.ready_buy')}
              {data !== undefined && <span className="n">{offers.length}</span>}
            </button>
            <button aria-selected={sub === 'holdings'} onClick={() => setSub('holdings')}>
              {t('market.can_ask')}
              {held.data !== undefined && <span className="n">{held.data.items.length}</span>}
            </button>
          </div>

          {sub === 'offers' && (
            <>
              {isLoading && (
                <>
                  <div className="skeleton" style={{ minHeight: 110 }} />
                  <div className="skeleton" style={{ minHeight: 110 }} />
                </>
              )}

              {error !== null && error !== undefined && (
                <div className="card card-dng text">{errorText(error)}</div>
              )}

              {/* Пустая витрина — не тупик: цену можно назвать свою, а рядом
                  во вкладке лежит то, что есть у людей. */}
              {data !== undefined && offers.length === 0 && (
                <div className="card col">
                  <strong className="title">{t('market.no_offers')}</strong>
                  <span className="text">{t('market.no_offers_hint')}</span>
                </div>
              )}

              {offers.map((d) => <OfferCard key={d.address} deal={d} onOpen={onOpen} />)}
            </>
          )}

          {sub === 'holdings' && (
            <Holdings
              collection={pick}
              floorOf={(collection) => {
                const row = collections.find((c) => sameAddress(c.collection, collection));
                return row?.marketFloor == null
                  ? null
                  : `${formatAmount(BigInt(row.marketFloor), row.marketDecimals, lang)} ${ticker(row.marketCurrency)}`;
              }}
              onAsk={(item) =>
                onRequest(item.collection, { address: item.item, name: item.name })
              }
            />
          )}

          {/* «Назовите свою цену» доступно всегда, а не только когда коллекция
              уже выбрана: человек, открывший витрину, чаще всего ещё ничего не
              выбирал — а просить ему есть что. */}
          {collections.length > 0 && (
            <button
              className="btn btn-ghost"
              onClick={() => {
                if (chosen === null || chosen === undefined) setPicking('request');
                else onRequest(chosen.collection);
              }}
            >
              {t('market.ask_here')}
            </button>
          )}

          {picking !== null && (
            <CollectionPicker
              collections={collections}
              onClose={() => setPicking(null)}
              onPick={(collection) => {
                const goal = picking;
                setPicking(null);
                if (goal === 'request') onRequest(collection);
                else setPick(collection);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
